// Message lookup: the Traditional Chinese source text is the key (gettext style), so
// code stays readable, a missing translation falls back to the source, and the same
// dictionary translates API error messages. `scripts/check-i18n.mjs` keeps every
// locale complete. See docs/I18N.md for the authoring rules.
import { DEFAULT_LOCALE, LOCALE_CONFIG, type Locale } from "./locales";

/** Plural forms follow Intl.PluralRules categories; `other` is required. */
export type PluralMessage = { zero?: string; one?: string; two?: string; few?: string; many?: string; other: string };
export type Message = string | PluralMessage;
export type Messages = Record<string, Message>;
export type MessageParams = Record<string, string | number | null | undefined>;

/** Separator between a context and the source text (gettext msgctxt convention). */
export const CONTEXT_SEPARATOR = "\u0004";
export const contextKey = (context: string, source: string) => `${context}${CONTEXT_SEPARATOR}${source}`;

const PLACEHOLDER = /\{(\w+)\}/g;

export function formatMessage(template: string, params?: MessageParams): string {
  if (!params) return template;
  return template.replace(PLACEHOLDER, (match, name: string) => {
    const value = params[name];
    return value === undefined || value === null ? match : String(value);
  });
}

const pluralRules = new Map<string, Intl.PluralRules>();
function selectPlural(message: PluralMessage, locale: Locale, count: number): string {
  const intl = LOCALE_CONFIG[locale].intl;
  let rules = pluralRules.get(intl);
  if (!rules) pluralRules.set(intl, rules = new Intl.PluralRules(intl));
  if (count === 0 && message.zero !== undefined) return message.zero;
  return message[rules.select(count) as keyof PluralMessage] ?? message.other;
}

export type Translator = {
  locale: Locale;
  /** Translate a source string; `{name}` placeholders are filled from params. Plural entries use params.count. */
  t: (source: string, params?: MessageParams) => string;
  /** Same as t, but disambiguated when one Chinese source needs different translations. */
  tx: (context: string, source: string, params?: MessageParams) => string;
  has: (source: string) => boolean;
};

export function createTranslator(locale: Locale, messages: Messages | null | undefined): Translator {
  const resolve = (key: string, fallback: string, params?: MessageParams) => {
    const entry = locale === DEFAULT_LOCALE ? undefined : messages?.[key];
    if (entry === undefined) return formatMessage(fallback, params);
    if (typeof entry === "string") return formatMessage(entry, params);
    // Plural choice: params.count, else params.n, else the first numeric param ({points}…).
    const numeric = params ? Object.values(params).find((value) => typeof value === "number") : undefined;
    const count = Number(params?.count ?? params?.n ?? numeric ?? 0);
    return formatMessage(selectPlural(entry, locale, count), params);
  };
  return {
    locale,
    t: (source, params) => resolve(source, source, params),
    tx: (context, source, params) => {
      const keyed = contextKey(context, source);
      return messages && locale !== DEFAULT_LOCALE && messages[keyed] !== undefined
        ? resolve(keyed, source, params)
        : resolve(source, source, params);
    },
    has: (source) => locale === DEFAULT_LOCALE || messages?.[source] !== undefined,
  };
}

/**
 * API errors are written "台灣用語。／香港用語。" by the Worker; the part before the
 * separator is the translation key. Returns the zh-TW source part.
 */
export function apiMessageSource(message: string): string {
  const separator = message.indexOf("。／");
  return separator >= 0 ? message.slice(0, separator + 1) : message;
}

/**
 * Marks a Chinese string as a translation key without translating it — for shared
 * constants (species names, default reasons) that the web app shows with t(value)
 * and the Worker with translatorFor(locale).t(value). Returns the source unchanged.
 */
export const msg = <T extends string>(source: T): T => source;
